import { useQuery } from '@apollo/client';
import { getBackendLanguage } from '@assets/i18n/config';
import {
  type PortfolioTemplate,
  PortfolioTemplateState,
} from '@interfaces/financialProduct';
import { MenuItem } from '@mui/material';
import { SelectField } from '@ovComponents/2-component/selectField/selectField';
import { PieChartTemplates } from '@ovComponents/3-pattern/pieChartTemplates/pieChartTemplates';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreateModelPortfolioOptions } from '../createModelPortfolio.types';
import { FETCH_PORTFOLIO_TEMPLATES } from '../queries';

type SelectPortfolioTemplateProps = {
  options: CreateModelPortfolioOptions['selectTemplate'];
  loading: boolean;
  continueFunc: () => void | Promise<void>;
  workflowCompletion?: {
    workflow?: {
      id: string;
      name: string;
    };
    id: string;
    objectId: string;
    objectType: string;
  };
  selectedPortfolioTemplate: PortfolioTemplate | undefined;
  onTemplateSelect: (template: PortfolioTemplate) => void;
  onCreateNew: () => void;
};

export const SelectPortfolioTemplate = ({
  options,
  loading,
  continueFunc,
  workflowCompletion,
  selectedPortfolioTemplate,
  onTemplateSelect,
  onCreateNew,
}: SelectPortfolioTemplateProps) => {
  const { t } = useTranslation(['shared', 'modelPortfolios']);
  const [focused, setFocused] = useState<string[]>([]);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { activeOrganization } = useContext(UserContext);
  const [templateData, setTemplateData] = useState<PortfolioTemplate[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const menuListRef = useRef<HTMLUListElement>(null);

  const {
    loading: templateLoading,
    refetch,
    fetchMore,
  } = useQuery(FETCH_PORTFOLIO_TEMPLATES, {
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization?.id || '',
          state: PortfolioTemplateState.ACTIVE,
        },
        pagination: {
          offset: 0,
          perPage: 25,
          sortDesc: true,
          sortField: 'createdAt',
          collation: {
            locale: getBackendLanguage(),
            strength: 1,
          },
        },
      },
    },
    skip: !activeOrganization?.id,
    onCompleted: (response) => {
      const templates =
        response?.fetchPortfolioTemplates?.portfolioTemplates || [];
      const count = response?.fetchPortfolioTemplates?.totalCount || 0;
      setTemplateData(templates);
      setTotalCount(count);

      if (selectedPortfolioTemplate) {
        const updatedTemplate = templates.find(
          (template: PortfolioTemplate) =>
            template.id === selectedPortfolioTemplate.id,
        );
        if (updatedTemplate) {
          onTemplateSelect(updatedTemplate);
        }
      }
    },
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: correct dependencies
  useEffect(() => {
    if (activeOrganization?.id) {
      // Reset local state before refetching to start fresh
      setTemplateData([]);
      setTotalCount(0);
      refetch();
    }
  }, [activeOrganization?.id]);

  // Refetch if a template is selected but not in the current list (e.g., newly created)
  // biome-ignore lint/correctness/useExhaustiveDependencies: correct dependencies
  useEffect(() => {
    if (
      selectedPortfolioTemplate &&
      templateData.length > 0 &&
      !templateData.find((t) => t.id === selectedPortfolioTemplate.id)
    ) {
      // A template was selected that's not in our current list, refetch to include it
      setTemplateData([]);
      setTotalCount(0);
      refetch();
    }
  }, [selectedPortfolioTemplate?.id]);

  const hasMoreTemplates = templateData.length < totalCount;

  const loadMoreTemplates = async () => {
    if (loadingMore || !hasMoreTemplates) return;

    setLoadingMore(true);
    try {
      await fetchMore({
        variables: {
          input: {
            filter: {
              organizationId: activeOrganization?.id || '',
              state: PortfolioTemplateState.ACTIVE,
            },
            pagination: {
              offset: templateData.length,
              perPage: 25,
              sortDesc: true,
              sortField: 'createdAt',
              collation: {
                locale: getBackendLanguage(),
                strength: 1,
              },
            },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previousResult;

          const newTemplates =
            fetchMoreResult.fetchPortfolioTemplates?.portfolioTemplates || [];
          const combinedTemplates = [
            ...(previousResult.fetchPortfolioTemplates?.portfolioTemplates ||
              []),
            ...newTemplates,
          ];

          setTemplateData(combinedTemplates);

          return {
            fetchPortfolioTemplates: {
              ...fetchMoreResult.fetchPortfolioTemplates,
              portfolioTemplates: combinedTemplates,
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const handleMenuScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const target = event.target as HTMLUListElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Load more when user scrolls to 80% of the list
    if (
      scrollTop + clientHeight >= scrollHeight * 0.8 &&
      hasMoreTemplates &&
      !loadingMore
    ) {
      loadMoreTemplates();
    }
  };

  const validate = () => {
    if (options.templateSelector.enabled && !selectedPortfolioTemplate) {
      setFocused(['templateName']);
      return false;
    }
    return true;
  };

  const submit = async () => {
    if (validate()) {
      sendAnalytic(
        ovAnalyticsEvents.workflowsSelectPortfolioTemplateContinueButtonSelect,
        {
          workflowStepTitle:
            options.templateSelector.label[getBackendLanguage()],
          workflowId: workflowCompletion?.workflow?.id,
          workflowName: workflowCompletion?.workflow?.name,
          activeWorkflowCompletionId: workflowCompletion?.id,
          objectId: workflowCompletion?.objectId,
          objectType: workflowCompletion?.objectType,
        },
      );

      // Track step 2 next analytics
      sendAnalytic(
        ovAnalyticsEvents.modelPageCreateStep2BuildAssetTemplateNext,
        {
          selectedTemplate: selectedPortfolioTemplate?.id,
          timestamp: new Date().toISOString(),
        },
      );

      await Promise.resolve(continueFunc());
    }
  };

  const handleCreateNew = () => {
    sendAnalytic(
      ovAnalyticsEvents.workflowsSelectPortfolioTemplateCreateNewButtonSelect,
      {
        workflowStepTitle: options.templateSelector.label[getBackendLanguage()],
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      },
    );

    // Track step 2 create new analytics
    sendAnalytic(
      ovAnalyticsEvents.modelPageCreateStep2BuildAssetTemplateCreateNew,
      {
        timestamp: new Date().toISOString(),
      },
    );

    onCreateNew();
  };

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled: loading || !selectedPortfolioTemplate,
        dataTestId: 'select-portfolio-template-continue-button',
      }}
      buttonFooter={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'flex-end',
          }}
        >
          <Button
            data-testid='create-new-template-button'
            onClick={handleCreateNew}
            disabled={loading}
            variant='contained'
            sx={{ mr: 2, textAlign: 'center' }}
            color='secondary'
          >
            {t('modelPortfolios:createNewTemplate')}
          </Button>
        </Box>
      }
    >
      {options.label[getBackendLanguage()] && (
        <Typography
          variant='displayLarge'
          sx={{
            mt: 1,
            mb: 2,
            fontWeight: 400,
            fontSize: '40px',
            fontFamily: 'Tiempos Text',
          }}
          data-testid='select-portfolio-template-title'
        >
          {options.label[getBackendLanguage()]}
          {options.description[getBackendLanguage()] && (
            <Typography
              variant='bodyLarge'
              sx={{ mt: 1 }}
              data-testid='select-portfolio-template-description'
            >
              {options.description[getBackendLanguage()]}
            </Typography>
          )}
        </Typography>
      )}

      <Grid container spacing={2}>
        {options.templateSelector.enabled && (
          <Grid size={12}>
            <SelectField
              testId='portfolio-template'
              onChange={(e: any) => {
                const selectedTemplate = templateData.find(
                  (template) => template.id === e.target.value,
                );
                if (selectedTemplate) {
                  onTemplateSelect(selectedTemplate);
                }
                setFocused([]);
              }}
              disabled={loading || templateLoading}
              label={options.templateSelector.label[getBackendLanguage()] || ''}
              placeholder={
                options.templateSelector.label[getBackendLanguage()] || ''
              }
              fullWidth
              value={selectedPortfolioTemplate?.id || ''}
              onBlur={() => setFocused([...focused, 'templateSelector'])}
              error={
                !selectedPortfolioTemplate &&
                focused.includes('templateSelector')
              }
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    onScroll: handleMenuScroll,
                    ref: menuListRef,
                    sx: { maxHeight: 300 },
                  },
                },
              }}
            >
              {templateData.map((template) => (
                <MenuItem
                  key={template.id}
                  value={template.id}
                  data-testid={`template-${kebabCase(template.translatedName?.[getBackendLanguage()] || '')}`}
                >
                  {template.translatedName?.[getBackendLanguage()]}
                </MenuItem>
              ))}
              {loadingMore && (
                <MenuItem disabled>
                  <Box
                    sx={{
                      justifyContent: 'center',
                      width: '100%',
                      display: 'flex',
                    }}
                  >
                    <CircularProgress size='medium' />
                  </Box>
                </MenuItem>
              )}
            </SelectField>
          </Grid>
        )}

        {options.assetClassAllocation.enabled && (
          <Grid size={12}>
            <PieChartTemplates
              assetClassBranches={
                selectedPortfolioTemplate?.assetClassBranches || []
              }
              variant='outlined'
              testId='portfolio-template-pie-chart'
            />
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};
